import React, { useMemo, useState } from 'react';
import useElementWidth from './useElementWidth';
import '../../assets/styles/WordNetwork.css';

const PADDING = 48;

const nodeRadius = (count, maxCount) => 7 + 17 * Math.sqrt(count / maxCount);

const labelBox = (node, r) => ({
  halfWidth: Math.max(r, node.label.length * 3.8 + 4),
  top: r,
  bottom: r + 20
});

const separateLabels = (nodes, pos, width, height, maxCount) => {
  const boxes = nodes.map((node) => labelBox(node, nodeRadius(node.count, maxCount)));
  for (let pass = 0; pass < 80; pass += 1) {
    let moved = false;
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = boxes[i];
        const b = boxes[j];
        const dx = pos[j].x - pos[i].x;
        const dy = pos[j].y - pos[i].y;
        const overlapX = a.halfWidth + b.halfWidth + 6 - Math.abs(dx);
        const overlapY = (dy >= 0 ? a.bottom + b.top : b.bottom + a.top) + 4 - Math.abs(dy);
        if (overlapX <= 0 || overlapY <= 0) continue;
        moved = true;
        if (overlapX < overlapY) {
          const shift = (overlapX / 2) * (dx >= 0 ? 1 : -1);
          pos[i].x -= shift;
          pos[j].x += shift;
        } else {
          const shift = (overlapY / 2) * (dy >= 0 ? 1 : -1);
          pos[i].y -= shift;
          pos[j].y += shift;
        }
      }
    }
    nodes.forEach((_, i) => {
      const box = boxes[i];
      pos[i].x = Math.min(width - box.halfWidth - 8, Math.max(box.halfWidth + 8, pos[i].x));
      pos[i].y = Math.min(height - box.bottom - 6, Math.max(box.top + 8, pos[i].y));
    });
    if (!moved) break;
  }
};

const layoutNetwork = (nodes, edges, width, height) => {
  const count = nodes.length;
  if (!count || !width) return {};

  const area = (width - PADDING * 2) * (height - PADDING * 2);
  const k = Math.sqrt(area / count) * 0.6;
  const index = new Map(nodes.map((node, i) => [node.id, i]));
  const pos = nodes.map((_, i) => {
    const angle = (i / count) * Math.PI * 2;
    const ring = 0.3 + (i % 3) * 0.12;
    return {
      x: width / 2 + Math.cos(angle) * (width / 2 - PADDING) * ring,
      y: height / 2 + Math.sin(angle) * (height / 2 - PADDING) * ring
    };
  });
  const maxWeight = Math.max(1, ...edges.map((e) => e.weight));
  let temperature = width / 8;

  for (let iter = 0; iter < 320; iter += 1) {
    const disp = pos.map(() => ({ x: 0, y: 0 }));

    for (let i = 0; i < count; i += 1) {
      for (let j = i + 1; j < count; j += 1) {
        const dx = pos[i].x - pos[j].x;
        const dy = pos[i].y - pos[j].y;
        const dist = Math.max(0.01, Math.hypot(dx, dy));
        const force = (k * k) / dist;
        disp[i].x += (dx / dist) * force;
        disp[i].y += (dy / dist) * force;
        disp[j].x -= (dx / dist) * force;
        disp[j].y -= (dy / dist) * force;
      }
    }

    edges.forEach((edge) => {
      const a = index.get(edge.source);
      const b = index.get(edge.target);
      if (a === undefined || b === undefined) return;
      const dx = pos[a].x - pos[b].x;
      const dy = pos[a].y - pos[b].y;
      const dist = Math.max(0.01, Math.hypot(dx, dy));
      const force = ((dist * dist) / k) * (0.4 + 0.6 * (edge.weight / maxWeight));
      disp[a].x -= (dx / dist) * force;
      disp[a].y -= (dy / dist) * force;
      disp[b].x += (dx / dist) * force;
      disp[b].y += (dy / dist) * force;
    });

    for (let i = 0; i < count; i += 1) {
      disp[i].x += (width / 2 - pos[i].x) * 0.04;
      disp[i].y += (height / 2 - pos[i].y) * 0.04;
      const length = Math.max(0.01, Math.hypot(disp[i].x, disp[i].y));
      pos[i].x += (disp[i].x / length) * Math.min(length, temperature);
      pos[i].y += (disp[i].y / length) * Math.min(length, temperature);
      pos[i].x = Math.min(width - PADDING, Math.max(PADDING, pos[i].x));
      pos[i].y = Math.min(height - PADDING, Math.max(PADDING, pos[i].y));
    }

    temperature *= 0.985;
  }

  separateLabels(nodes, pos, width, height, Math.max(1, ...nodes.map((n) => n.count)));

  return Object.fromEntries(nodes.map((node, i) => [node.id, pos[i]]));
};

const WordNetwork = ({ nodes = [], edges = [], height = 440, labels = {} }) => {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [canvasRef, width] = useElementWidth();
  const boxHeight = width && width < 560 ? Math.max(height, 480) : height;

  const positions = useMemo(() => layoutNetwork(nodes, edges, width, boxHeight), [nodes, edges, width, boxHeight]);
  const maxCount = Math.max(1, ...nodes.map((n) => n.count));
  const maxWeight = Math.max(1, ...edges.map((e) => e.weight));
  const radius = (node) => nodeRadius(node.count, maxCount);

  const neighbours = useMemo(() => {
    const map = new Map(nodes.map((node) => [node.id, []]));
    edges.forEach((edge) => {
      if (map.has(edge.source)) map.get(edge.source).push({ id: edge.target, weight: edge.weight });
      if (map.has(edge.target)) map.get(edge.target).push({ id: edge.source, weight: edge.weight });
    });
    map.forEach((list) => list.sort((a, b) => b.weight - a.weight));
    return map;
  }, [nodes, edges]);

  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const focus = hovered || selected;
  const focusSet = focus ? new Set([focus, ...(neighbours.get(focus) || []).map((n) => n.id)]) : null;
  const selectedNode = selected ? byId.get(selected) : null;

  if (!nodes.length) return null;

  return (
    <div className="tp-network">
      <div ref={canvasRef} className="tp-network__canvas" style={{ minHeight: boxHeight }}>
        {width > 0 && (
          <svg
            className="tp-network__svg"
            width={width}
            height={boxHeight}
            viewBox={`0 0 ${width} ${boxHeight}`}
            role="group"
            aria-label={labels.graph}
            onClick={() => setSelected(null)}
          >
            <g>
              {edges.map((edge) => {
                const a = positions[edge.source];
                const b = positions[edge.target];
                if (!a || !b) return null;
                const active = focus && (edge.source === focus || edge.target === focus);
                return (
                  <line
                    key={`${edge.source}-${edge.target}`}
                    x1={a.x}
                    y1={a.y}
                    x2={b.x}
                    y2={b.y}
                    className={`tp-network__edge ${active ? 'is-active' : ''} ${focus && !active ? 'is-dim' : ''}`.replace(/\s+/g, ' ').trim()}
                    strokeWidth={1 + 4 * (edge.weight / maxWeight)}
                  />
                );
              })}
            </g>
            <g>
              {nodes.map((node) => {
                const p = positions[node.id];
                if (!p) return null;
                const r = radius(node);
                const state = !focusSet ? '' : node.id === focus ? 'is-focus' : focusSet.has(node.id) ? 'is-near' : 'is-dim';
                return (
                  <g
                    key={node.id}
                    className={`tp-network__node ${state} ${selected === node.id ? 'is-selected' : ''}`.replace(/\s+/g, ' ').trim()}
                    transform={`translate(${p.x} ${p.y})`}
                    tabIndex={0}
                    role="button"
                    aria-pressed={selected === node.id}
                    aria-label={labels.node ? labels.node(node) : node.label}
                    onMouseEnter={() => setHovered(node.id)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(node.id)}
                    onBlur={() => setHovered(null)}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelected((current) => (current === node.id ? null : node.id));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelected((current) => (current === node.id ? null : node.id));
                      }
                    }}
                  >
                    <circle r={r} />
                    <text y={r + 14} textAnchor="middle">{node.label}</text>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </div>

      <aside className="tp-network__panel" aria-live="polite">
        {selectedNode ? (
          <>
            <p className="tp-network__panel-word">{selectedNode.label}</p>
            <p className="tp-network__panel-meta">{labels.mentions ? labels.mentions(selectedNode) : selectedNode.count}</p>
            <p className="tp-network__panel-title">{labels.appearsWith}</p>
            <ul className="tp-network__panel-list">
              {(neighbours.get(selectedNode.id) || []).slice(0, 8).map((n) => (
                <li key={n.id}>
                  <button type="button" className="tp-network__chip" onClick={() => setSelected(n.id)}>
                    {byId.get(n.id)?.label}
                    <span>{n.weight}</span>
                  </button>
                </li>
              ))}
              {!(neighbours.get(selectedNode.id) || []).length && (
                <li className="tp-network__panel-empty">{labels.noLinks}</li>
              )}
            </ul>
          </>
        ) : (
          <p className="tp-network__hint">{labels.hint}</p>
        )}
      </aside>
    </div>
  );
};

export default WordNetwork;
export { WordNetwork };
